import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiClientError, type ChatView, type UserView } from '@olmt/shared'
import { addChatMembers, loadDirectory } from '../lib/data'
import { useT } from '../lib/i18n'
import { addableMembers } from '../lib/chat'
import { useAuthStore } from '../stores/authStore'
import { Avatar } from './Avatar'
import { IconButton } from './IconButton'
import { Spinner } from './Spinner'
import { CloseIcon } from './Icons'

type AddMembersPickerProps = {
  chat: ChatView
  onClose: () => void
  /** The members were added; the caller refreshes the row from the returned `ChatView`. */
  onAdded: () => void
}

/**
 * "Add people" for a group, over `GET /api/users` (CONTRACT §4 *Directory*) — the same source the
 * `NewChatPicker` and `InvitePicker` read. The offers are `addableMembers`: the directory minus
 * everyone already in the chat and the viewer, so confirming posts only new memberships (the api is
 * idempotent either way, but a list that keeps offering the people who are already here reads as
 * broken).
 *
 * The modal shape is `InvitePicker`'s: bottom sheet on a phone, centred panel on desktop, focus on
 * open, Escape closes, Tab is kept inside. It is a dialog, not a route, because it is a step in the
 * details screen rather than a place of its own.
 */
export function AddMembersPicker({ chat, onClose, onAdded }: AddMembersPickerProps) {
  const t = useT()
  const meId = useAuthStore((state) => state.user?.id ?? null)
  const [directory, setDirectory] = useState<UserView[] | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    loadDirectory()
      .then((users) => {
        if (!cancelled) setDirectory(users)
      })
      .catch(() => {
        if (!cancelled) setError(t('chat.couldNotLoadMembers'))
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current
    dialog?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialog) return
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const candidates = useMemo(
    () => (directory ? addableMembers(chat, meId, directory) : null),
    [chat, meId, directory],
  )

  const toggle = (userId: string) => {
    setSelected((previous) =>
      previous.includes(userId) ? previous.filter((id) => id !== userId) : [...previous, userId],
    )
  }

  const add = async () => {
    if (selected.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await addChatMembers(chat.id, selected)
      onAdded()
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : t('chat.couldNotAddMembers'))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-app/80 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-members-heading"
        tabIndex={-1}
        className="sheet-enter flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-panel bg-surface pb-[var(--sab)] outline-none shadow-lift ring-1 ring-line-strong sm:rounded-panel sm:pb-0"
      >
        <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <h2 id="add-members-heading" className="min-w-0 flex-1 truncate text-title font-semibold text-ink">
            {t('chat.addPeople')}
          </h2>
          <IconButton label={t('chat.close')} size={36} onClick={onClose}>
            <CloseIcon size={18} />
          </IconButton>
        </header>

        <p role="status" className="px-4 pt-3 text-ui text-muted">
          {selected.length === 0 ? t('chat.chooseWhoToAdd') : t('chat.selected', { count: selected.length })}
        </p>

        {error && (
          <p role="alert" className="px-4 pt-2 text-meta text-danger">
            {error}
          </p>
        )}

        <div className="chat-scroll mt-2 min-h-0 flex-1">
          {candidates === null ? (
            <div className="flex justify-center p-6">
              <Spinner label={t('chat.loadingMembers')} />
            </div>
          ) : candidates.length === 0 ? (
            <p className="p-6 text-center text-ui text-muted">{t('chat.everyoneAlreadyIn')}</p>
          ) : (
            <ul className="flex flex-col p-2">
              {candidates.map((user) => {
                const picked = selected.includes(user.id)
                return (
                  <li key={user.id}>
                    <label
                      className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-panel px-2 py-2 transition-colors duration-quick ${
                        picked ? 'bg-surface2' : 'hover:bg-surface2/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="size-5 shrink-0 accent-accent"
                        checked={picked}
                        disabled={busy}
                        onChange={() => toggle(user.id)}
                      />
                      <Avatar name={user.displayName} path={user.avatarUrl} size={40} online={user.online} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-ui text-ink">
                          <bdi>{user.displayName}</bdi>
                        </span>
                        <span className="block truncate text-meta text-muted">@{user.handle}</span>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-line p-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 flex-1 rounded-full text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2"
          >
            {t('chat.cancel')}
          </button>
          <button
            type="button"
            disabled={selected.length === 0 || busy}
            onClick={() => void add()}
            className="min-h-11 flex-1 rounded-full bg-accent px-4 text-ui font-semibold press text-on-accent hover:bg-accent-strong disabled:opacity-40"
          >
            {busy
              ? t('chat.adding')
              : selected.length > 1
                ? t('chat.addPeopleCount', { count: selected.length })
                : t('chat.add')}
          </button>
        </div>
      </div>
    </div>
  )
}
